// Full-screen sign-in gate. Rendered by App.tsx when there's no
// signed-in user. Initializes GIS, renders the official Google
// button, and on success calls setUser. The user's Google display
// name becomes the channel's author label — no separate "display
// name" entry needed.
//
// When VITE_GOOGLE_CLIENT_ID is unset, shows a clear "operator
// setup needed" message instead of a broken sign-in surface.
//
// Mirrors agicore-foundry's gate; styling adapted to the Playground's
// light register.

import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../store/authStore";
import {
  initializeGoogleAuth, renderSignInButton, googleAuthClientId,
} from "../lib/google-auth";

export function SignInGate() {
  const setUser = useAuthStore((s) => s.setUser);
  const setReady = useAuthStore((s) => s.setReady);
  const ready = useAuthStore((s) => s.ready);
  const [error, setError] = useState<string | null>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  const clientId = googleAuthClientId();

  useEffect(() => {
    if (!clientId) {
      setError("VITE_GOOGLE_CLIENT_ID is not configured. Add it to .env.local (dev) or the Cloudflare Worker environment (prod).");
      return;
    }
    let cancelled = false;
    const container = btnRef.current;
    initializeGoogleAuth({
      clientId,
      onSignIn: (user) => {
        setUser(user);
      },
    }).then(async () => {
      if (cancelled) return;
      setReady(true);
      if (container) {
        try {
          await renderSignInButton(container);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    }).catch((e) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
    });
    return () => {
      cancelled = true;
      // Belt + suspenders: GIS mutates the button container with
      // iframes; clear them before React unmounts so the teardown
      // doesn't trip over nodes React never tracked.
      if (container) {
        while (container.firstChild) container.removeChild(container.firstChild);
      }
    };
  }, [clientId, setUser, setReady]);

  return (
    <div className="gate-root">
      <div className="gate-card">
        <div className="gate-mark">
          <span className="gate-dot" />
          BabyAI Playground
        </div>
        <div className="gate-tagline">team chat · BYOK · zero servers</div>
        <div className="gate-explain">
          Sign in with Google to use the Playground.
          <br />
          Your provider keys stay in this browser — Google sign-in just gives
          you a display name in shared channels.
        </div>
        {clientId && !ready && <div className="gate-loading">loading google sign-in…</div>}
        {clientId && (
          <div
            className="gate-button"
            ref={btnRef}
            dangerouslySetInnerHTML={{ __html: "" }}
          />
        )}
        {error && (
          <div className="gate-error">
            <div className="gate-error-title">can't sign in</div>
            <div className="gate-error-body">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
}
